import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { TabIndices } from '../../common/i-doc/i-doc.registry.ts';
import { PageCard } from '../common/PageCard';
import { useClientOptions } from '../../hooks/useClientOptions';
import { questionnairesConstructionService } from '../../services/questionnaires-construction/questionnaires-construction.service';
import { ConstructionTemplate, AssignedClient } from '../../types/questionnaire-construction';
import { getExpertBulkNotificationPreview } from '../../services/notification.service';
import { toastService } from '../../services/toast.service';

const STATUS_LABELS: Record<AssignedClient['status'], string> = {
  in_progress: 'В процесі',
  completed: 'Завершено',
};

export const ClientsQuestionnaireDistributionTab: React.FC = () => {
  const { groups } = useClientOptions();

  const [templates, setTemplates] = useState<ConstructionTemplate[]>([]);
  const [hasDrafts, setHasDrafts] = useState(false);
  const [loadingTemplates, setLoadingTemplates] = useState(true);
  const [templateId, setTemplateId] = useState<number | ''>('');

  const [targetType, setTargetType] = useState<'all' | 'groups'>('all');
  const [selectedGroupId, setSelectedGroupId] = useState<number | ''>('');
  const [recipients, setRecipients] = useState<{ id: number; name: string }[]>([]);
  const [loadingRecipients, setLoadingRecipients] = useState(false);

  const [sending, setSending] = useState(false);
  const [assignedClients, setAssignedClients] = useState<AssignedClient[]>([]);
  const [loadingAssigned, setLoadingAssigned] = useState(false);

  useEffect(() => {
    questionnairesConstructionService.getTemplates()
      .then(list => {
        const own = list.filter(t => !t.isSystem);
        setTemplates(own.filter(t => t.status === 'published'));
        setHasDrafts(own.some(t => t.status === 'draft'));
      })
      .finally(() => setLoadingTemplates(false));
  }, []);

  useEffect(() => {
    const fetchRecipients = async () => {
      if (targetType === 'groups' && !selectedGroupId) {
        setRecipients([]);
        return;
      }
      setLoadingRecipients(true);
      try {
        const list = await getExpertBulkNotificationPreview({
          targetType,
          groupId: targetType === 'groups' ? (selectedGroupId as number) : undefined,
        });
        setRecipients(list);
      } catch {
        toastService.error('Не вдалося завантажити список отримувачів');
      } finally {
        setLoadingRecipients(false);
      }
    };
    fetchRecipients();
  }, [targetType, selectedGroupId]);

  useEffect(() => {
    if (!templateId) {
      setAssignedClients([]);
      return;
    }
    setLoadingAssigned(true);
    questionnairesConstructionService.getAssignedClients(templateId)
      .then(setAssignedClients)
      .catch(() => toastService.error('Не вдалося завантажити список призначених клієнтів'))
      .finally(() => setLoadingAssigned(false));
  }, [templateId]);

  const alreadyAssignedIds = new Set(assignedClients.map(a => a.clientId));
  const newRecipients = recipients.filter(r => !alreadyAssignedIds.has(r.id));

  const handleSend = async () => {
    if (!templateId || newRecipients.length === 0) return;
    if (targetType === 'groups' && !selectedGroupId) return;

    setSending(true);
    try {
      const result = await questionnairesConstructionService.assignTemplate(
        templateId,
        recipients.map(r => r.id),
      );
      if (result.assigned.length > 0) {
        toastService.success(`Призначено ${result.assigned.length} клієнт(ам)`);
      }
      if (result.alreadyAssigned.length > 0) {
        toastService.info(`${result.alreadyAssigned.length} клієнт(ів) уже мали це опитування`);
      }
      const updated = await questionnairesConstructionService.getAssignedClients(templateId);
      setAssignedClients(updated);
    } catch {
      toastService.error('Не вдалося призначити опитування');
    } finally {
      setSending(false);
    }
  };

  return (
    <Box data-i-doc={TabIndices.CLIENTS_QUESTIONNAIRE_DISTRIBUTION}>
      <PageCard>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, maxWidth: 640 }}>
          <Box>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              1. Кому призначити?
              {loadingRecipients && <CircularProgress size={14} />}
            </Typography>
            <RadioGroup
              row
              value={targetType}
              onChange={(e) => setTargetType(e.target.value as 'all' | 'groups')}
            >
              <FormControlLabel value="all" control={<Radio />} label="Всім клієнтам" />
              <FormControlLabel value="groups" control={<Radio />} label="Вибраній групі" />
            </RadioGroup>
          </Box>

          {targetType === 'groups' && (
            <FormControl fullWidth size="small">
              <InputLabel>Виберіть групу</InputLabel>
              <Select
                value={selectedGroupId}
                label="Виберіть групу"
                onChange={(e) => setSelectedGroupId(e.target.value as number)}
              >
                {groups.map(g => (
                  <MenuItem key={g.id} value={g.id}>{g.name}</MenuItem>
                ))}
              </Select>
            </FormControl>
          )}

          <Box>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
              {templateId
                ? `Буде призначено новим: ${newRecipients.length} з ${recipients.length} обраних`
                : `Обрано отримувачів: ${recipients.length}`}
            </Typography>
            <Box sx={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: 0.5,
              maxHeight: 100,
              overflowY: 'auto',
              p: 1,
              bgcolor: 'action.hover',
              borderRadius: 1,
              border: '1px dashed',
              borderColor: 'divider',
            }}>
              {recipients.length > 0 ? (
                recipients.map(r => {
                  const already = alreadyAssignedIds.has(r.id);
                  const chip = (
                    <Chip
                      key={r.id}
                      label={r.name}
                      size="small"
                      variant="outlined"
                      icon={already ? <CheckCircleIcon fontSize="small" /> : undefined}
                      color={already ? 'success' : 'default'}
                      sx={{ bgcolor: already ? undefined : 'background.paper', opacity: already ? 0.7 : 1 }}
                    />
                  );
                  return already ? (
                    <Tooltip key={r.id} title="Вже має доступ до цього опитування">{chip}</Tooltip>
                  ) : chip;
                })
              ) : (
                <Typography variant="caption" color="error">
                  {loadingRecipients ? 'Завантаження...' : 'Немає активних отримувачів у вибраній категорії'}
                </Typography>
              )}
            </Box>
          </Box>

          <Typography variant="subtitle2" color="text.secondary">2. Яке опитування призначити?</Typography>
          {!loadingTemplates && templates.length === 0 ? (
            <Alert
              severity="info"
              action={
                <Button size="small" component={Link} to="/quiz-builder">
                  {hasDrafts ? 'Опублікувати →' : 'Створити →'}
                </Button>
              }
            >
              {hasDrafts
                ? 'Усі ваші опитування ще чернетки — опублікуйте хоча б одне в конструкторі, щоб призначати клієнтам.'
                : 'У вас ще немає власних опитувань. Створіть опитування в конструкторі, щоб призначати їх клієнтам.'}
            </Alert>
          ) : (
            <FormControl fullWidth size="small">
              <InputLabel>Опитування</InputLabel>
              <Select
                value={templateId}
                label="Опитування"
                onChange={(e) => setTemplateId(e.target.value as number)}
                disabled={loadingTemplates}
              >
                {templates.map(t => (
                  <MenuItem key={t.id} value={t.id}>{t.nestNameI18n?.uk ?? `Опитувальник #${t.id}`}</MenuItem>
                ))}
              </Select>
            </FormControl>
          )}

          <Box>
            <Button
              variant="contained"
              startIcon={sending ? <CircularProgress size={18} color="inherit" /> : <SendIcon />}
              onClick={handleSend}
              disabled={sending || !templateId || (targetType === 'groups' && !selectedGroupId) || newRecipients.length === 0}
            >
              {sending ? 'Призначається...' : `Розіслати (${newRecipients.length})`}
            </Button>
          </Box>
        </Box>
      </PageCard>

      {templateId !== '' && (
        <PageCard sx={{ mt: 2 }}>
          <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1 }}>Кому вже призначено</Typography>
          {loadingAssigned ? (
            <Box display="flex" justifyContent="center" sx={{ py: 4 }}>
              <CircularProgress />
            </Box>
          ) : assignedClients.length === 0 ? (
            <Typography color="text.secondary">Ще нікому не призначено це опитування.</Typography>
          ) : (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Клієнт</TableCell>
                  <TableCell>Статус</TableCell>
                  <TableCell>Прогрес</TableCell>
                  <TableCell>Призначено</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {assignedClients.map(a => (
                  <TableRow key={a.clientId} hover>
                    <TableCell>{a.name}</TableCell>
                    <TableCell>
                      <Chip
                        label={STATUS_LABELS[a.status]}
                        size="small"
                        color={a.status === 'completed' ? 'success' : 'default'}
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell>{a.completionRate ?? 0}%</TableCell>
                    <TableCell>{new Date(a.assignedAt).toLocaleDateString('uk-UA')}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </PageCard>
      )}
    </Box>
  );
};
